/**
 * EEGSignal
 *
 * SHS Visual Experience System — Sprint 29.2B.
 *
 * Second member of the SHS physiology visual language. A stylized, abstract
 * suggestion of low-amplitude, mixed-frequency brain electrical activity —
 * designed to make an SHS surface feel "alive" alongside the AirflowSignal
 * without simulating any medical monitor.
 *
 * NON-DIAGNOSTIC / NON-CLINICAL / NON-USER — same contract as AirflowSignal:
 *   - Not real EEG data.
 *   - Carries no numeric values, no channel labels, no interpretation.
 *   - Not linked to any user, session, or clinical decision.
 *   - `aria-hidden` defaults to true; decorative only.
 *
 * Reduced-motion contract:
 *   Reads useMotionMode() and omits the drift class when `isReduced` is
 *   true. Static waveform renders in place. No second reduced-motion
 *   system was introduced — inherits Sprint 29.1A architecture.
 *
 * SVG technique:
 *   A single deterministic path sampled at 3-SVG-unit intervals across
 *   4 identical periods of P = 600 units. Each period is the sum of four
 *   sine components at cycles-per-period {5, 7, 11, 17} — coprime by
 *   design, so the combined trace is quasi-random within a period yet
 *   exactly periodic across periods. Translating the group by exactly
 *   one period produces a seamless infinite loop with no visible seam.
 *
 * Composition (versus AirflowSignal — Sprint 29.2A):
 *   - Airflow: broader, slower, respiratory (6 s per period).
 *   - EEG:     finer, quicker, mixed-frequency (11 s per period).
 *   The two durations are coprime (LCM = 66 s beat) so the eye never
 *   perceives a shared rhythm.
 *
 * Living Hero governance:
 *   Per Sprint 29.2B: MAXIMUM TWO continuously animated physiological
 *   signals may coexist in a single hero composition unless explicitly
 *   authorized. Adding a third continuous signal without written Founder
 *   authorization is prohibited.
 *
 * Cost:
 *   - Zero JS run-loop; the animation is a single CSS keyframe on <g>.
 *   - Path built once via useMemo and reused for the lifetime of the
 *     component. Path length ≈ 800 `L` commands (line segments); ~4 KB
 *     of SVG source — fully compressible.
 *   - `will-change: transform` (in CSS) promotes to a compositor layer.
 */

import React, { useMemo } from 'react';
import { useMotionMode } from '../hooks/useMotionMode';

// -- Geometry (SVG user-space units) ----------------------------------------

const EEG_PERIOD = 600;
const EEG_COUNT = 4;
const EEG_VIEWBOX_WIDTH = EEG_PERIOD * EEG_COUNT;
const EEG_VIEWBOX_HEIGHT = 60;               // shorter viewport → finer line
const EEG_BASELINE = 30;
const EEG_SAMPLE_STEP = 3;

// Cycles per EEG_PERIOD. Coprime for quasi-random appearance while staying
// exactly periodic (required for a seamless loop). Amplitudes chosen so the
// combined maximum excursion is ≤ ~10 SVG units, i.e. one-sixth of the
// viewBox height — deliberately low-amplitude.
const EEG_HARMONICS = [
    { cycles: 5,  amp: 3.5, phase: 0 },
    { cycles: 7,  amp: 2.5, phase: 1.7 },
    { cycles: 11, amp: 2.0, phase: 3.3 },
    { cycles: 17, amp: 1.2, phase: 0.9 },
];

function buildEEGPath() {
    const totalWidth = EEG_VIEWBOX_WIDTH;
    let d = `M 0,${EEG_BASELINE.toFixed(2)}`;
    for (let x = EEG_SAMPLE_STEP; x <= totalWidth; x += EEG_SAMPLE_STEP) {
        const tPeriod = (x / EEG_PERIOD) * 2 * Math.PI;
        let y = EEG_BASELINE;
        for (const h of EEG_HARMONICS) {
            y += h.amp * Math.sin(h.cycles * tPeriod + h.phase);
        }
        d += ` L ${x.toFixed(1)},${y.toFixed(2)}`;
    }
    return d;
}

/**
 * EEGSignal
 *
 * Props mirror AirflowSignal so both components are interchangeable in
 * composition:
 *   className   extra classes on the outer <svg> — callers control
 *               position, size, opacity, and color (via text-* since
 *               the stroke uses currentColor).
 *   style       inline style overrides.
 *   ariaHidden  defaults true (decorative).
 *   strokeWidth override stroke thickness in SVG user units. Default 1.5,
 *               finer than Airflow's 2.
 *
 * Reserved for future phases:
 *   intensity   reserved API surface for a future Clinical Mode variant;
 *               ignored today.
 */
export const EEGSignal = ({
    className = '',
    style,
    ariaHidden = true,
    strokeWidth = 1.5,
    ...rest
}) => {
    const { isReduced } = useMotionMode();
    const d = useMemo(buildEEGPath, []);
    const groupClass = isReduced ? '' : 'shs-eeg-drift';

    return (
        <svg
            aria-hidden={ariaHidden}
            focusable="false"
            viewBox={`0 0 ${EEG_VIEWBOX_WIDTH} ${EEG_VIEWBOX_HEIGHT}`}
            preserveAspectRatio="xMidYMid slice"
            className={className}
            style={style}
            {...rest}
        >
            <g className={groupClass}>
                <path
                    d={d}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                />
            </g>
        </svg>
    );
};

export default EEGSignal;
